import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LifeBuoy, X, Send, CheckCircle2 } from "lucide-react";

const NAVY = "#0b192c";
const GOLD = "#c4a454";
const MUTED = "#475569";
const BORDER = "#e2e1dd";

// The widget is intentionally rendered on EVERY page (auth or not) so a locked-out
// practice admin can reach Corpzo even when they can't sign in.
export default function HelpWidget() {
  const { user } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState(""); // catches bots
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const location = useLocation();

  // Prefill from the signed-in user (if any)
  useEffect(() => {
    if (user?.name) setName(user.name);
    if (user?.email) setEmail(user.email);
  }, [user?.id, user?.email, user?.name]);

  // Reset the "sent" success state a few seconds after close so it doesn't linger
  useEffect(() => {
    if (!open && sent) {
      const t = setTimeout(() => setSent(false), 400);
      return () => clearTimeout(t);
    }
  }, [open, sent]);

  const contextRole = useMemo(() => (
    user?.active_role || (user?.roles?.[0] ?? "")
  ), [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || message.trim().length < 8) {
      return toast.error("Add your name, email and a short message (min 8 chars).");
    }
    setSending(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/support/help`,
        {
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
          page: (typeof window !== "undefined" ? window.location.href : location?.pathname) || "",
          role: contextRole || null,
          practice: user?.practice_name || null,
          website: honeypot, // honeypot — real users leave this empty
        },
        { withCredentials: true },
      );
      setSent(true);
      setMessage("");
      toast.success("Message sent · Corpzo will reply to your email");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send — please try again");
    } finally { setSending(false); }
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[9998] rounded-full shadow-lg hover:shadow-xl transition-all
                     px-4 py-3 flex items-center gap-2 font-semibold text-sm"
          style={{ background: NAVY, color: "white" }}
          data-testid="help-widget-open">
          <LifeBuoy size={16} style={{ color: GOLD }} />
          Need help?
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-[9999] w-[min(380px,calc(100vw-24px))] rounded-xl shadow-2xl border overflow-hidden"
          style={{ background: "white", borderColor: BORDER }}
          data-testid="help-widget-panel">
          <div className="flex items-center justify-between px-4 py-3"
               style={{ background: NAVY, color: "white" }}>
            <div className="flex items-center gap-2">
              <LifeBuoy size={16} style={{ color: GOLD }} />
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: GOLD }}>
                  Corpzo support
                </div>
                <div className="text-sm font-semibold">We usually reply within a few hours</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="help-widget-close"
                    className="text-white/70 hover:text-white">
              <X size={16} />
            </button>
          </div>

          {sent ? (
            <div className="p-6 text-center">
              <div className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: "#166534" }}>
                <CheckCircle2 size={18} /> Message sent
              </div>
              <p className="text-sm mt-2" style={{ color: MUTED }}>
                Corpzo will reply directly to <b>{email}</b>. Watch your inbox (and spam folder just in case).
              </p>
              <Button onClick={() => setOpen(false)}
                      className="mt-4 bg-[#0b192c] hover:bg-[#0b192c]/90 text-white"
                      data-testid="help-widget-done">
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="help-name" className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: MUTED }}>
                    Name
                  </Label>
                  <Input id="help-name" required value={name}
                         onChange={(e) => setName(e.target.value)}
                         className="mt-1 h-9 text-sm" data-testid="help-widget-name" />
                </div>
                <div>
                  <Label htmlFor="help-email" className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: MUTED }}>
                    Email
                  </Label>
                  <Input id="help-email" required type="email" value={email}
                         onChange={(e) => setEmail(e.target.value)}
                         className="mt-1 h-9 text-sm" data-testid="help-widget-email" />
                </div>
              </div>
              <div>
                <Label htmlFor="help-message" className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: MUTED }}>
                  How can we help?
                </Label>
                <textarea id="help-message" required rows={4}
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          placeholder="Locked out, billing question, feature request…"
                          className="w-full mt-1 border rounded p-2 text-sm bg-white"
                          style={{ borderColor: BORDER, color: NAVY }}
                          data-testid="help-widget-message" />
              </div>
              {/* Honeypot — hidden from users, catches bots */}
              <input type="text" tabIndex={-1} autoComplete="off"
                     value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
                     style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                     aria-hidden="true" />
              <Button type="submit" disabled={sending}
                      className="w-full bg-[#0b192c] hover:bg-[#0b192c]/90 text-white font-semibold h-10"
                      data-testid="help-widget-send">
                <Send size={13} className="mr-1.5" />
                {sending ? "Sending…" : "Send message"}
              </Button>
              <p className="text-[10px] leading-relaxed text-center" style={{ color: MUTED }}>
                Reply goes to <b>{email || "your email"}</b>. Corpzo replies from
                {" "}<span className="font-mono">reach@aifledger.com</span>.
              </p>
            </form>
          )}
        </div>
      )}
    </>
  );
}
